import React, { useState, useEffect, useMemo, useRef } from 'react';
import { GastoService } from '../../services/administration/gastoService';
import { InventarioService } from '../../services/administration/inventarioService';
import { ConceptoPicker } from './ConceptoPicker';
import { ConceptoPrimerUsoModal } from './ConceptoPrimerUsoModal';
import { getCurrentUserInfo, getRepositories } from '../../services/storage';
import { getLocalISODate } from '../../utils/dateHelper';
import { formatMoney } from '../../utils/formatNumber';
import { Concepto, Categoria, TipoCategoria } from '../../types/administracion';
import { formatearStockFraccional, kgDeCantidad } from '../../helpers/conceptosHelper';
import UbicacionAutocompleteInput from '../common/UbicacionAutocompleteInput';
import { usePermissions } from '../../hooks/usePermissions';
import { Enums } from '../../types/enums';
import { ContraparteInput, ContraparteValor, camposDeContraparte } from '../common/ContraparteInput';

/** Valores iniciales (ej. "Cargar monto" de un pago anotado sin monto). */
export interface UnifiedTransactionPrefill {
  fecha?: string;
  descripcion?: string;
  detalle?: string;
  contraparte?: ContraparteValor | null;
}

interface UnifiedTransactionFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
  initialType?: TransactionType;
  prefill?: UnifiedTransactionPrefill;
}

type TransactionType = 'GASTO' | 'COMPRA';

/** Tipos de categoría permitidos en el tab Gasto (spec §5.1); COMPRA filtra por gestion=inventario */
const TIPOS_GASTO: TipoCategoria[] = [
  'alimentacion_engorde',
  'otros_costos_engorde',
  'fijo',
  'otro',
];

interface FormData {
  fecha: string;
  montoTotal: number | null;
  asignarA: TransactionType;
  ubicacion_id?: string;
  ubicacion_nombre?: string;
  cantidad?: number;
  descripcion: string;
  /** Detalle opcional del concepto (P3a): viaja en la columna texto `concepto` del DTO */
  detalle: string;
  /**
   * Override manual "es de engorde" (fix-3a, tri-state): null = según concepto
   * (default), true/false = el usuario flipeó el toggle y su decisión gana.
   */
  esEngordeOverride: boolean | null;
}

const UNIDAD_DEFAULT = 'unidad';

/**
 * Alimento (categoría alimentacion_engorde) con PB/EM faltantes (P3a): además
 * de los parámetros estimados, el modal de primer uso se abre para pedirle al
 * usuario los valores de la etiqueta. Sin esto, un alimento migrado sin
 * nutrición quedaba silenciosamente sin PB/EM para siempre.
 */
const esAlimentoSinNutricion = (c: Concepto, categorias: Map<string, Categoria>) =>
  categorias.get(c.categoria_id)?.tipo === 'alimentacion_engorde' &&
  (!c.nutricion || c.nutricion.pb == null || c.nutricion.em == null);

export const UnifiedTransactionForm: React.FC<UnifiedTransactionFormProps> = ({
  onSuccess,
  onCancel,
  initialType = 'GASTO',
  prefill
}) => {
  // Gasto y consumo escriben en gastos/ingresos/stocks_historico, que en RLS
  // exigen financial.create — sin el permiso el registro quedaría encolado
  // como CONFLICTO para siempre, así que el guardado se bloquea acá también.
  const { can: canPermission } = usePermissions();
  const sinPermisoEscritura = !canPermission('financial', 'create');
  const [formData, setFormData] = useState<FormData>({
    fecha: prefill?.fecha || getLocalISODate(),
    montoTotal: null,
    asignarA: initialType, // Default to Consumption/Gasto
    ubicacion_id: undefined,
    ubicacion_nombre: undefined,
    cantidad: undefined,
    descripcion: prefill?.descripcion || '',
    detalle: prefill?.detalle || '',
    esEngordeOverride: null
  });
  // A quién se le pagó / a quién se le compró (2026-10-18). Opcional.
  const [contraparte, setContraparte] = useState<ContraparteValor | null>(prefill?.contraparte || null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [empresaId, setEmpresaId] = useState<string>('');
  // Texto del autocomplete de corral (el id viaja en formData.ubicacion_id)
  const [ubicacionTexto, setUbicacionTexto] = useState('');

  // Taxonomía (spec §5.1): un solo campo Concepto; unidad/gestión derivan del elegido
  const [conceptoElegido, setConceptoElegido] = useState<Concepto | null>(null);
  const [categorias, setCategorias] = useState<Categoria[]>([]);
  // Race (P3b Task 9): hasta que listarParaEmpresa resuelve, el Map de
  // categorías está vacío y esAlimentoSinNutricion no puede detectar un
  // alimento sin PB/EM → el modal de primer uso no dispararía. Se bloquea el
  // picker (disabled + hint) hasta resolver, en vez de re-chequear después.
  const [categoriasCargadas, setCategoriasCargadas] = useState(false);
  const [modalConcepto, setModalConcepto] = useState<Concepto | null>(null);
  const conceptoActivoRef = useRef<Concepto | null>(null);

  // Load empresa_id from profiles, fallback to first available empresa
  useEffect(() => {
    const loadEmpresaId = async () => {
      try {
        const currentUser = await getCurrentUserInfo();
        let empresaIdToUse = currentUser?.empresaId;

        // If no empresa in profile, try to get first available empresa
        if (!empresaIdToUse) {
          const { empresa } = getRepositories();
          const empresas = await empresa.getAll();
          if (empresas.length > 0) {
            empresaIdToUse = empresas[0].id;
            console.log('Using first available empresa:', empresas[0].nombre, empresaIdToUse);
          }
        }

        if (empresaIdToUse) {
          setEmpresaId(empresaIdToUse);
        } else {
          console.error('No active empresa found in profiles or empresas table');
          setError('No se encontró una empresa activa. Creá una empresa primero.');
        }
      } catch (err) {
        console.error('Error loading empresa_id:', err);
        setError('Error al cargar la información de la empresa.');
      }
    };

    loadEmpresaId();
  }, []);

  // Caché de categorías para resolver el nombre en el submit (backward compat del DTO)
  useEffect(() => {
    if (!empresaId) return;
    let cancelled = false;
    (async () => {
      try {
        const { categoria } = getRepositories();
        const cats = await categoria.listarParaEmpresa(empresaId);
        if (!cancelled) setCategorias(cats);
      } catch (err) {
        console.error('Error cargando categorías:', err);
      } finally {
        // Aunque falle, se desbloquea el picker: sin categorías el alta igual
        // funciona (solo decae la resolución de nombre/modal de primer uso)
        if (!cancelled) setCategoriasCargadas(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [empresaId]);

  // Cada tab tiene su conjunto de conceptos (gasto vs inventario) → al cambiar, se re-elige
  useEffect(() => {
    setConceptoElegido(null);
    conceptoActivoRef.current = null;
    setFormData(prev => ({ ...prev, detalle: '', esEngordeOverride: null }));
  }, [formData.asignarA]);

  const categoriasById = useMemo(() => new Map(categorias.map(c => [c.id, c])), [categorias]);

  const nombreCategoria = conceptoElegido
    ? categoriasById.get(conceptoElegido.categoria_id)?.nombre ?? ''
    : '';

  // Carga fraccional (spec §3.7): la cantidad se carga en la unidad de carga, el kg se deriva
  const tieneCarga =
    !!conceptoElegido?.unidad_carga && conceptoElegido.unidad_carga !== 'kg';
  const unidadMedida = conceptoElegido
    ? tieneCarga
      ? conceptoElegido.unidad_carga!
      : conceptoElegido.unidad
    : UNIDAD_DEFAULT;
  const etiquetaGestion = conceptoElegido
    ? conceptoElegido.gestion === 'inventario'
      ? 'Inventario'
      : 'Gasto directo'
    : '';

  // Override "es de engorde" tri-state (fix-3a): el default deriva del tipo de
  // la categoría del concepto (fijo/otro → off, alimentación/otros costos de
  // engorde → on); si el usuario flipeó el toggle, su decisión GANA. Al cambiar
  // de concepto el default se re-deriva solo (el override, si hay, se respeta).
  const tipoCategoriaElegida = conceptoElegido
    ? categoriasById.get(conceptoElegido.categoria_id)?.tipo
    : undefined;
  const esEngordeDerivado =
    tipoCategoriaElegida === 'alimentacion_engorde' ||
    tipoCategoriaElegida === 'otros_costos_engorde';
  const esEngordeEfectivo = formData.esEngordeOverride ?? esEngordeDerivado;

  /**
   * Selección de concepto: unidad/gestión quedan derivadas del concepto y, si es
   * un global o un migrado con parámetros estimados (§5.6), se abre el modal de
   * primer uso. Con Detalle (P3a): un match parcial del buscador ("flete
   * alfalfa" → Flete) pre-llena el Detalle con el resto.
   */
  const handleConceptoChange = async (c: Concepto | null, detallePrefill?: string) => {
    conceptoActivoRef.current = c;
    setConceptoElegido(c);
    setModalConcepto(null);
    setFormData(prev =>
      c
        ? { ...prev, detalle: detallePrefill ?? '' }
        : { ...prev, detalle: '' }
    );

    if (!c || !empresaId) return;
    // El modal aplica a globales y a migrados (propios con origen 'migracion',
    // que llegan de la migración con kg default 450 sin confirmar)
    const esGlobal = c.empresa_id === null;
    const esMigrado = c.origen === 'migracion';
    if (!esGlobal && !esMigrado) return;
    const conParametrosEstimados =
      (!!c.unidad_carga && c.unidad_carga !== 'kg') || !!c.nutricion;
    // Además del OR: un alimento con PB/EM faltantes también lo abre, aunque
    // no tenga parámetros estimados que confirmar (P3a)
    if (!conParametrosEstimados && !esAlimentoSinNutricion(c, categoriasById)) return;

    try {
      const { concepto } = getRepositories();
      const confirmado = await concepto.estaConfirmado(c.id, empresaId);
      // Si el usuario cambió de concepto mientras se consultaba, no abrir el modal
      if (!confirmado && conceptoActivoRef.current?.id === c.id) {
        setModalConcepto(c);
      }
    } catch (err) {
      console.error('Error verificando confirmación del concepto:', err);
      // Nunca bloquea el flujo: se usa el global sin confirmar
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (sinPermisoEscritura) {
      setError('No tenés permiso para cargar gastos, compras ni consumos');
      return;
    }
    setLoading(true);
    setError(null);

    try {
      // Validation
      if (!conceptoElegido) throw new Error('El concepto es requerido');
      if (formData.montoTotal === null || formData.montoTotal <= 0) throw new Error('El monto total debe ser mayor a 0');

      if (formData.asignarA === 'COMPRA') {
        if (!formData.cantidad || formData.cantidad <= 0) {
          throw new Error('Para inventario, la cantidad es requerida');
        }

        const precioUnitario = (formData.montoTotal || 0) / formData.cantidad;

        // La cantidad queda en la unidad de carga si el concepto la define (spec §3.7):
        // la verdad es lo que el usuario carga; el kg siempre se deriva.
        const payload: Parameters<typeof InventarioService.registrarCompra>[0] & {
          concepto_id?: string;
        } = {
          fecha_compra: formData.fecha,
          nombre: conceptoElegido.nombre,
          categoria: nombreCategoria,
          concepto_id: conceptoElegido.id,
          cantidad: formData.cantidad,
          precio_unitario: precioUnitario,
          unidad_medida: unidadMedida,
          ubicacion_id: formData.ubicacion_id,
          descripcion: formData.descripcion.trim() || undefined,
          empresaId: empresaId,
          userId: undefined,
          ...camposDeContraparte(contraparte),
        };

        await InventarioService.registrarCompra(payload);
      } else {
        const payload: Parameters<typeof GastoService.registrarGasto>[0] & {
          concepto_id?: string;
        } = {
          fecha_evento: formData.fecha,
          // Columna texto repropiada (P3a): sin detalle → nombre del concepto (como hoy)
          concepto: formData.detalle.trim() || conceptoElegido.nombre,
          categoria: nombreCategoria || undefined,
          concepto_id: conceptoElegido.id,
          monto: formData.montoTotal,
          // El corral sólo tiene sentido para gastos de engorde (picker visible
          // sólo ahí); guard por si quedó un valor viejo en el estado.
          ubicacion_id: esEngordeEfectivo ? formData.ubicacion_id : undefined,
          descripcion: formData.descripcion.trim(),
          empresaId: empresaId,
          userId: undefined,
          // Override tri-state (fix-3a): la key viaja SOLO cuando difiere del
          // derivado del concepto — sin override explícito no se escribe nada
          // (NULL en DB = según concepto) y el payload queda compatible con
          // DBs viejas sin la columna.
          ...(esEngordeEfectivo !== esEngordeDerivado ? { es_engorde: esEngordeEfectivo } : {}),
          ...camposDeContraparte(contraparte),
        };

        await GastoService.registrarGasto(payload);
      }

      // Reset or Success
      if (onSuccess) onSuccess();

      setFormData({
        fecha: getLocalISODate(),
        montoTotal: 0,
        asignarA: 'GASTO',
        ubicacion_id: undefined,
        ubicacion_nombre: undefined,
        cantidad: undefined,
        descripcion: '',
        detalle: '',
        esEngordeOverride: null
      });

      setUbicacionTexto('');
      setContraparte(null);

      setConceptoElegido(null);
      conceptoActivoRef.current = null;
    } catch (err: any) {
      console.error('Error processing transaction:', err);
      setError(err.message || 'Error al procesar la operación');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-paper rounded-xl shadow-card">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-ink">Cargar Gasto / Compra</h2>
        <p className="text-sage text-sm">Registre sus gastos o compras de inventario en un solo lugar</p>
      </div>

      {error && (
        <div className="border-l-4 border-danger bg-danger/10 p-4 mb-6 rounded-r">
          <div className="flex">
            <div className="flex-shrink-0">
              <span className="text-danger">⚠️</span>
            </div>
            <div className="ml-3">
              <p className="text-sm text-danger">{error}</p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* 1. Concepto y Monto (Always Visible) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div data-ar-guide="gasto-form-concepto">
            <label htmlFor="unified-concepto" className="block text-sm font-medium text-ink mb-1">
              Concepto <span className="text-danger">*</span>
            </label>
            <ConceptoPicker
              id="unified-concepto"
              value={conceptoElegido}
              onChange={handleConceptoChange}
              tiposPermitidos={formData.asignarA === 'GASTO' ? TIPOS_GASTO : undefined}
              soloInventario={formData.asignarA === 'COMPRA'}
              disabled={!categoriasCargadas}
              placeholder={
                !categoriasCargadas
                  ? 'Cargando categorías…'
                  : formData.asignarA === 'COMPRA'
                    ? 'Buscar o crear producto de inventario…'
                    : 'Ej: Gasoil, Flete, Megafardo…'
              }
            />
            {!categoriasCargadas && (
              <p className="mt-1 text-xs text-sage">Cargando categorías…</p>
            )}
            {formData.asignarA === 'GASTO' && (
              <div className="mt-3">
                <label htmlFor="unified-detalle" className="block text-sm font-medium text-ink mb-1">
                  Detalle <span className="text-sage font-normal">(opcional)</span>
                </label>
                <input
                  type="text"
                  id="unified-detalle"
                  data-ar-guide="gasto-form-detalle"
                  className="w-full rounded-md border border-hairline bg-white px-3 py-2 text-ink placeholder:text-sage/70 focus:border-forest focus:outline-none focus:ring-2 focus:ring-forest/20"
                  value={formData.detalle}
                  onChange={(e) => setFormData({ ...formData, detalle: e.target.value })}
                  placeholder="Ej: proveedor, destino, lote…"
                />
              </div>
            )}
          </div>

          <div>
            <label htmlFor="unified-monto" className="block text-sm font-medium text-ink mb-1">
              Monto Total <span className="text-danger">*</span>
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-sage sm:text-sm">$</span>
              </div>
              <input
                type="number"
                id="unified-monto"
                data-ar-guide="gasto-form-monto"
                className="w-full rounded-md border border-hairline bg-white pl-7 py-2 text-ink placeholder:text-sage/70 text-lg font-semibold focus:border-forest focus:outline-none focus:ring-2 focus:ring-forest/20"
                value={formData.montoTotal ?? ''}
                onChange={(e) => setFormData({ ...formData, montoTotal: e.target.value === '' ? null : parseFloat(e.target.value) })}
                placeholder="0.00"
                min="0.01"
                step="0.01"
                required
              />
            </div>
          </div>
        </div>

        {/* 2. Asignación (The Pivot) */}
        <div className="bg-paper-2/40 p-4 rounded-lg border border-hairline" data-ar-guide="gasto-form-asignacion">
          <label className="block text-sm font-medium text-ink mb-3">
            Asignar a: <span className="text-danger">*</span>
          </label>
          <div className="flex space-x-4">
            <label className={`flex-1 cursor-pointer rounded-lg border p-4 flex items-center justify-center transition-all ${
              formData.asignarA === 'GASTO'
                ? 'bg-paper border-forest ring-2 ring-forest/20 shadow-card'
                : 'bg-paper-2 border-hairline hover:bg-hairline/50'
            }`}>
              <input
                type="radio"
                name="asignarA"
                value="GASTO"
                checked={formData.asignarA === 'GASTO'}
                onChange={() => setFormData({ ...formData, asignarA: 'GASTO' })}
                className="sr-only"
              />
              <div className="text-center">
                <span className="text-2xl block mb-1">💸</span>
                <span className="font-medium text-ink">Consumo Inmediato</span>
                <span className="text-xs text-sage block mt-1">Gasto directo (Combustible, Viáticos)</span>
              </div>
            </label>

            <label className={`flex-1 cursor-pointer rounded-lg border p-4 flex items-center justify-center transition-all ${
              formData.asignarA === 'COMPRA'
                ? 'bg-paper border-forest ring-2 ring-forest/20 shadow-card'
                : 'bg-paper-2 border-hairline hover:bg-hairline/50'
            }`}>
              <input
                type="radio"
                name="asignarA"
                value="COMPRA"
                checked={formData.asignarA === 'COMPRA'}
                onChange={() => {
                  // El corral sólo aplica a gastos de engorde (no a compras de
                  // inventario): al pivotear se limpia para no filtrarse al stock.
                  setUbicacionTexto('');
                  setFormData({ ...formData, asignarA: 'COMPRA', ubicacion_id: undefined });
                }}
                className="sr-only"
              />
              <div className="text-center">
                <span className="text-2xl block mb-1">📦</span>
                <span className="font-medium text-ink">Inventario</span>
                <span className="text-xs text-sage block mt-1">Queda guardado (Postes, Alimento)</span>
              </div>
            </label>
          </div>
        </div>

        {/* 3. Datos del concepto (derivados, read-only) + destino del gasto.
            La clase de gasto VIVE en la categoría del concepto (spec §12.4),
            pero este gasto puntual puede forzarse con el segmented control
            (fix-3a, tri-state): arranca según el concepto y tocar la opción
            opuesta fija el override manual (asignar un fijo — p.ej. alquiler
            del campo — al cálculo de engorde). */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {conceptoElegido ? (
            <>
              <div>
                <label htmlFor="unified-unidad" className="block text-sm font-medium text-ink mb-1">
                  Unidad
                </label>
                <input
                  id="unified-unidad"
                  type="text"
                  className="w-full rounded-md border border-hairline bg-hairline/40 px-3 py-2 text-ink cursor-not-allowed"
                  // La unidad que el usuario opera: si el concepto tiene unidad de
                  // carga (§3.7) va esa, no la base interna kg del motor nutricional
                  value={unidadMedida}
                  disabled
                  readOnly
                />
              </div>
              <div>
                <label htmlFor="unified-gestion" className="block text-sm font-medium text-ink mb-1">
                  Gestión
                </label>
                <input
                  id="unified-gestion"
                  type="text"
                  className="w-full rounded-md border border-hairline bg-hairline/40 px-3 py-2 text-ink cursor-not-allowed"
                  value={etiquetaGestion}
                  disabled
                  readOnly
                />
              </div>
              {formData.asignarA === 'GASTO' && (
                <div className="md:col-span-2">
                  <span className="block text-sm font-medium text-ink mb-2">
                    Destino del gasto
                  </span>
                  <div
                    id="unified-es-engorde"
                    data-ar-guide="gasto-form-es-engorde"
                    role="radiogroup"
                    aria-label="Destino del gasto"
                    className="grid grid-cols-2 gap-2"
                  >
                    <button
                      type="button"
                      role="radio"
                      aria-checked={!esEngordeEfectivo}
                      onClick={() => {
                        setFormData(prev =>
                          // Tocar la opción opuesta fija el override (gana sobre
                          // el concepto); tocar la activa no cambia nada.
                          esEngordeEfectivo
                            ? { ...prev, esEngordeOverride: false, ubicacion_id: undefined }
                            : prev
                        );
                        // Al volver a General el corral ya no aplica — se limpia.
                        if (esEngordeEfectivo) setUbicacionTexto('');
                      }}
                      className={`flex flex-col items-center gap-0.5 rounded-lg border px-4 py-2.5 text-sm font-medium transition-colors ${
                        !esEngordeEfectivo
                          ? 'bg-forest border-forest text-paper shadow-card'
                          : 'bg-paper-2 border-hairline text-sage hover:bg-hairline/50'
                      }`}
                    >
                      <span className="inline-flex items-center gap-2">
                        <span aria-hidden="true">🧭</span>
                        <span>General</span>
                      </span>
                      {!esEngordeEfectivo && (
                        <span className="text-xs font-normal text-paper/80">
                          no entra al costo de engorde
                        </span>
                      )}
                    </button>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={esEngordeEfectivo}
                      onClick={() =>
                        setFormData(prev =>
                          esEngordeEfectivo
                            ? prev
                            : { ...prev, esEngordeOverride: true }
                        )
                      }
                      className={`flex flex-col items-center gap-0.5 rounded-lg border px-4 py-2.5 text-sm font-medium transition-colors ${
                        esEngordeEfectivo
                          ? 'bg-forest border-forest text-paper shadow-card'
                          : 'bg-paper-2 border-hairline text-sage hover:bg-hairline/50'
                      }`}
                    >
                      <span className="inline-flex items-center gap-2">
                        <span aria-hidden="true">🐮</span>
                        <span>Engorde</span>
                      </span>
                      {esEngordeEfectivo && (
                        <span className="text-xs font-normal text-paper/80">
                          entra al costo de engorde
                        </span>
                      )}
                    </button>
                  </div>
                  <p className="mt-2 text-xs text-sage">
                    {formData.esEngordeOverride === null
                      ? 'Según la categoría del concepto — tocá para cambiar solo este gasto'
                      : `Cambio manual para este gasto — el resto de «${conceptoElegido.nombre}» sigue según su categoría`}
                  </p>
                  {/* Corral (opcional): costo directo a un corral puntual; sin
                      corral, el cálculo de engorde lo prorratea entre todos. */}
                  {esEngordeEfectivo && (
                    <div className="mt-3" data-ar-guide="gasto-form-corral">
                      <UbicacionAutocompleteInput
                        value={ubicacionTexto}
                        onChange={setUbicacionTexto}
                        onUbicacionIdChange={(id: string | null) =>
                          setFormData(prev => ({ ...prev, ubicacion_id: id ?? undefined }))
                        }
                        tiposPermitidos={[Enums.UBICACION_TIPO.CORRAL, 'CORRAL_ENGORDE']}
                        placeholder="Corral (opcional) — ej: Corral 1…"
                      />
                      <p className="mt-1 text-xs text-sage">
                        Opcional: elegí un corral y el costo va entero a ese corral. Si lo dejás
                        vacío, se reparte entre todos los corrales según los kilos que ganó cada uno.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-sage md:col-span-2">
              Al elegir un concepto, la unidad y la gestión se completan automáticamente.
            </p>
          )}
        </div>

        {/* Stock Specific Fields */}
        {formData.asignarA === 'COMPRA' && (
          <div className="bg-forest/5 p-4 rounded-lg border border-forest/20 animate-fade-in-down">
            <h4 className="text-sm font-semibold text-forest mb-3">Detalles de Inventario</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="unified-cantidad" className="block text-sm font-medium text-ink mb-1">
                  Cantidad{tieneCarga ? ` (${conceptoElegido?.unidad_carga})` : ''} <span className="text-danger">*</span>
                </label>
                <input
                  id="unified-cantidad"
                  type="number"
                  className="w-full rounded-md border border-hairline bg-white px-3 py-2 text-ink placeholder:text-sage/70 focus:border-forest focus:outline-none focus:ring-2 focus:ring-forest/20"
                  value={formData.cantidad ?? ''}
                  onChange={(e) => setFormData({ ...formData, cantidad: e.target.value === '' ? undefined : parseFloat(e.target.value) })}
                  placeholder={tieneCarga ? `0 ${conceptoElegido?.unidad_carga}` : '0'}
                  min="0.01"
                  step="0.01"
                  required
                />
                {tieneCarga && conceptoElegido && formData.cantidad && formData.cantidad > 0 && (
                  <p className="mt-2 text-sm text-forest" data-testid="equivalente-kg">
                    {formatearStockFraccional(kgDeCantidad(formData.cantidad, conceptoElegido), conceptoElegido)}
                  </p>
                )}
              </div>
            </div>
            {formData.cantidad && formData.cantidad > 0 && formData.montoTotal !== null && formData.montoTotal > 0 && (
              <div className="mt-2 text-sm text-forest text-right">
                Precio Unitario estimado: <strong>{formatMoney(formData.montoTotal / formData.cantidad)}</strong> / {unidadMedida}
              </div>
            )}
          </div>
        )}

        {/* Common Footer Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-ink mb-1">
              Fecha
            </label>
            <input
              type="date"
              className="w-full rounded-md border border-hairline bg-white px-3 py-2 text-ink focus:border-forest focus:outline-none focus:ring-2 focus:ring-forest/20"
              value={formData.fecha}
              onChange={(e) => setFormData({ ...formData, fecha: e.target.value })}
              max={getLocalISODate()}
            />
          </div>

          <ContraparteInput
            id="unified-contraparte"
            label="A quién"
            value={contraparte}
            onChange={setContraparte}
          />

          <div>
            <label className="block text-sm font-medium text-ink mb-1">
              Nota / Descripción
            </label>
            <input
              type="text"
              className="w-full rounded-md border border-hairline bg-white px-3 py-2 text-ink placeholder:text-sage/70 focus:border-forest focus:outline-none focus:ring-2 focus:ring-forest/20"
              value={formData.descripcion}
              onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
              placeholder="Opcional..."
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end space-x-4 pt-4 border-t border-hairline">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-3 text-ink bg-paper-2 rounded-xl hover:bg-hairline/50 transition-colors font-medium"
              disabled={loading}
            >
              Cancelar
            </button>
          )}
          <button
            type="submit"
            data-ar-guide="btn-save-transaction"
            disabled={loading || sinPermisoEscritura}
            title={sinPermisoEscritura ? 'No tenés permiso para cargar gastos, compras ni consumos' : undefined}
            className={`px-8 py-3 text-paper rounded-xl shadow-card transform transition-all font-bold text-lg flex items-center space-x-2 ${
              loading || sinPermisoEscritura ? 'opacity-50 cursor-not-allowed bg-hairline hover:scale-100' :
              'bg-forest hover:bg-forest-mid hover:scale-105'
            }`}
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-paper"></div>
                <span>Guardando...</span>
              </>
            ) : (
              <>
                <span>💾</span>
                <span>Guardar {formData.asignarA === 'COMPRA' ? 'Inventario' : 'Gasto'}</span>
              </>
            )}
          </button>
        </div>
      </form>

      {/* Modal de primer uso de concepto global o migrado (spec §5.6) */}
      {modalConcepto && empresaId && (
        <ConceptoPrimerUsoModal
          concepto={modalConcepto}
          empresaId={empresaId}
          pedirNutricionFaltante={esAlimentoSinNutricion(modalConcepto, categoriasById)}
          onConfirmado={() => setModalConcepto(null)}
          onVersionCreada={(c) => {
            conceptoActivoRef.current = c;
            setConceptoElegido(c);
            setModalConcepto(null);
          }}
          onCancelar={() => setModalConcepto(null)}
        />
      )}
    </div>
  );
};
